import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ApiResponse, PageableDto } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../api-config';
import { mapMutationMessage } from '../../utils/map-pageable';

/** Shape API de un archivo digital del customer. */
export interface CustomerDigitalFileDto {
  id: string;
  customerId?: string;
  name: string;
  validityDate?: string | null;
  noExpiration?: boolean;
  fileUrl?: string;
  originalName?: string;
  contentType?: string;
  fileSize?: number;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Metadatos del multipart (sin el File). */
export type CustomerDigitalFileWritePayload = {
  name: string;
  noExpiration: boolean;
  validityDate?: string | null;
};

export type CustomerDigitalFileListParams = {
  page?: number;
  size?: number;
  sort?: string;
  direction?: 'asc' | 'desc';
  status?: string;
  search?: string;
};

/**
 * Archivo digital del cliente.
 *   GET    /api/customers/{customerId}/digital-files
 *   POST   /api/customers/{customerId}/digital-files  (multipart)
 *   PUT    /api/customers/{customerId}/digital-files/{id}  (multipart)
 *   PATCH  /api/customers/{customerId}/digital-files/{id}/status
 *   DELETE /api/customers/{customerId}/digital-files/{id}
 */
@Injectable({ providedIn: 'root' })
export class CustomerDigitalFileService {
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private resource(customerId: string): string {
    return `${this.apiConfig.apiUrl}/customers/${customerId}/digital-files`;
  }

  list(
    customerId: string,
    params: CustomerDigitalFileListParams = {},
  ): Observable<CustomerDigitalFileDto[]> {
    let httpParams = new HttpParams()
      .set('page', String(params.page ?? 0))
      .set('size', String(params.size ?? 100))
      .set('sort', params.sort ?? 'createdAt')
      .set('direction', params.direction ?? 'desc');

    if (params.status?.trim()) {
      httpParams = httpParams.set('status', params.status.trim());
    }
    if (params.search?.trim()) {
      httpParams = httpParams.set('search', params.search.trim());
    }

    return this.http
      .get<ApiResponse<CustomerDigitalFileDto[] | PageableDto<CustomerDigitalFileDto>>>(
        this.resource(customerId),
        { params: httpParams },
      )
      .pipe(map((res) => this.unwrapList(res.data)));
  }

  create(
    customerId: string,
    payload: CustomerDigitalFileWritePayload,
    file: File,
  ): Observable<CustomerDigitalFileDto> {
    return this.http
      .post<ApiResponse<CustomerDigitalFileDto>>(
        this.resource(customerId),
        this.toFormData(payload, file),
      )
      .pipe(map((res) => res.data as CustomerDigitalFileDto));
  }

  update(
    customerId: string,
    fileId: string,
    payload: CustomerDigitalFileWritePayload,
    file?: File | null,
  ): Observable<CustomerDigitalFileDto> {
    return this.http
      .put<ApiResponse<CustomerDigitalFileDto>>(
        `${this.resource(customerId)}/${fileId}`,
        this.toFormData(payload, file),
      )
      .pipe(map((res) => res.data as CustomerDigitalFileDto));
  }

  updateStatus(
    customerId: string,
    fileId: string,
    status: 'active' | 'inactive',
  ): Observable<{ message: string }> {
    const apiStatus = status === 'inactive' ? 'INACTIVE' : 'ACTIVE';
    return this.http
      .patch<{ success?: boolean; message?: string }>(
        `${this.resource(customerId)}/${fileId}/status`,
        { status: apiStatus },
      )
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Estatus actualizado exitosamente'),
        ),
      );
  }

  delete(
    customerId: string,
    fileId: string,
  ): Observable<{ message: string }> {
    return this.http
      .delete<{ success?: boolean; message?: string }>(
        `${this.resource(customerId)}/${fileId}`,
      )
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Archivo eliminado exitosamente'),
        ),
      );
  }

  private toFormData(
    payload: CustomerDigitalFileWritePayload,
    file?: File | null,
  ): FormData {
    const formData = new FormData();
    formData.append('name', payload.name);
    formData.append('noExpiration', String(payload.noExpiration));
    if (!payload.noExpiration && payload.validityDate) {
      formData.append('validityDate', payload.validityDate);
    }
    if (file) {
      formData.append('file', file);
    }
    return formData;
  }

  private unwrapList(
    data:
      | CustomerDigitalFileDto[]
      | PageableDto<CustomerDigitalFileDto>
      | null
      | undefined,
  ): CustomerDigitalFileDto[] {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.content)) return data.content;
    return [];
  }
}
